<template>
  <div class="card m-3 text-center">
    <h4 class="card-header">{{ t('enter-sms-code') }}</h4>
    <div class="d-flex card-body justify-content-center">
      <div class="sms-code-form">
        <p class="hint">
          {{ t('sms-code-sent-to') }}
          <strong>{{ maskedPhone }}</strong>
        </p>

        <!-- Sześć osobnych pól na cyfry. Auto-tab przy wpisaniu, paste rozdzielany. -->
        <div class="code-inputs" @paste.prevent="onPaste">
          <input
            v-for="(_, idx) in 6"
            :key="idx"
            ref="digitRefs"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="1"
            class="code-digit"
            :class="{ 'is-invalid': hasError }"
            :value="digits[idx]"
            :disabled="isVerifying"
            @input="onDigitInput($event, idx)"
            @keydown="onDigitKeydown($event, idx)"
            @focus="onDigitFocus($event)"
          />
        </div>

        <!-- Countdown ważności kodu -->
        <p class="countdown" v-if="secondsLeft > 0">
          {{ t('sms-code-expires-in') }}
          <strong>{{ formattedCountdown }}</strong>
        </p>
        <p class="countdown text-danger" v-else>
          {{ t('sms-code-expired') }}
        </p>

        <div class="actions">
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="!canVerify"
            @click="onVerify"
          >
            <span v-show="isVerifying" class="spinner-border spinner-border-sm me-1"></span>
            {{ t('verify-code') }}
          </button>

          <button
            type="button"
            class="btn btn-link btn-sm"
            :disabled="resendCooldown > 0 || isResending"
            @click="onResend"
          >
            <span v-show="isResending" class="spinner-border spinner-border-sm me-1"></span>
            {{ resendCooldown > 0
              ? t('resend-sms-in', { seconds: resendCooldown })
              : t('resend-sms-code') }}
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useNotificationStore } from '@/stores/notification.store';

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();
const notify = useNotificationStore();

// TTL kodu po stronie BE = 600 s. Frontowy countdown jest tylko UX-em — prawdziwa
// weryfikacja ekspiracji jest po stronie BE (kod sprawdzony tam).
const CODE_TTL_SECONDS = 600;
// Throttle resend zsynchronizowany z BE (APP_SMS_RESET_REQUEST_THROTTLE = 60).
const RESEND_COOLDOWN_SECONDS = 60;

const digits = ref(['', '', '', '', '', '']);
const digitRefs = ref([]);
const isVerifying = ref(false);
const isResending = ref(false);
const hasError = ref(false);

const secondsLeft = ref(CODE_TTL_SECONDS);
const resendCooldown = ref(RESEND_COOLDOWN_SECONDS);

let ttlTimer = null;
let resendTimer = null;

// Defense-in-depth (jak w ChangePasswordView): blokuj wyciek do referrera.
const REFERRER_META_ID = 'sms-code-no-referrer';

onMounted(() => {
  // Brak numeru w stanie = ktoś wszedł bezpośrednio na URL bez przejścia przez request.
  // Odsyłamy na początek flow, żeby nie zostawiać widoku w niezdefiniowanym stanie.
  if (!authStore.pendingSmsReset) {
    router.replace('/reset-password');
    return;
  }

  if (!document.getElementById(REFERRER_META_ID)) {
    const meta = document.createElement('meta');
    meta.id = REFERRER_META_ID;
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
  }

  startTtlCountdown();
  startResendCooldown();
  nextTick(() => digitRefs.value[0]?.focus());
});

onBeforeUnmount(() => {
  document.getElementById(REFERRER_META_ID)?.remove();
  if (ttlTimer) clearInterval(ttlTimer);
  if (resendTimer) clearInterval(resendTimer);
});

const fullCode = computed(() => digits.value.join(''));
const canVerify = computed(
  () => fullCode.value.length === 6 && !isVerifying.value && secondsLeft.value > 0
);

const maskedPhone = computed(() => {
  const p = authStore.pendingSmsReset;
  if (!p) return '';
  const full = `${p.phoneNumberDir}${p.phoneNumber}`;
  // Maskujemy środek: "+48 *** *** 789"
  if (full.length < 8) return full;
  return `${full.slice(0, 3)} *** *** ${full.slice(-3)}`;
});

const formattedCountdown = computed(() => {
  const m = Math.floor(secondsLeft.value / 60);
  const s = secondsLeft.value % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
});

function startTtlCountdown() {
  ttlTimer = setInterval(() => {
    if (secondsLeft.value > 0) secondsLeft.value -= 1;
  }, 1000);
}

function startResendCooldown() {
  resendCooldown.value = RESEND_COOLDOWN_SECONDS;
  resendTimer = setInterval(() => {
    if (resendCooldown.value > 0) resendCooldown.value -= 1;
  }, 1000);
}

// Treść komunikatu idzie do globalnych powiadomień; lokalnie zostaje tylko
// `hasError`, bo od niego zależy czerwona obwódka pól z kodem.
function showError(msg) {
  hasError.value = true;
  notify.error(msg);
}

function showInfo(msg) {
  hasError.value = false;
  notify.success(msg);
}

function clearError() {
  hasError.value = false;
}

function onDigitInput(event, idx) {
  const v = event.target.value.replace(/\D/g, '').slice(0, 1);
  digits.value[idx] = v;
  event.target.value = v;
  clearError();
  if (v && idx < 5) {
    digitRefs.value[idx + 1]?.focus();
  }
}

function onDigitKeydown(event, idx) {
  if (event.key === 'Backspace' && !digits.value[idx] && idx > 0) {
    digitRefs.value[idx - 1]?.focus();
  } else if (event.key === 'ArrowLeft' && idx > 0) {
    digitRefs.value[idx - 1]?.focus();
  } else if (event.key === 'ArrowRight' && idx < 5) {
    digitRefs.value[idx + 1]?.focus();
  } else if (event.key === 'Enter' && canVerify.value) {
    onVerify();
  }
}

function onDigitFocus(event) {
  event.target.select();
}

function onPaste(event) {
  const text = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
  if (!text) return;
  for (let i = 0; i < 6; i++) {
    digits.value[i] = text[i] || '';
  }
  clearError();
  const lastIdx = Math.min(text.length, 6) - 1;
  nextTick(() => digitRefs.value[Math.min(lastIdx + 1, 5)]?.focus());
}

async function onVerify() {
  if (!canVerify.value) return;
  isVerifying.value = true;
  clearError();
  try {
    const token = await authStore.verifySmsResetCode(fullCode.value);
    if (!token) {
      showError(t('sms-code-invalid-or-expired'));
      // Wyczyść inputy żeby ułatwić ponowne wpisanie
      digits.value = ['', '', '', '', '', ''];
      nextTick(() => digitRefs.value[0]?.focus());
      return;
    }
    // Sukces — przerzucamy na ten sam ChangePasswordView, którego używa flow email.
    authStore.clearPendingSmsReset();
    router.push({
      path: '/reset-password/reset',
      query: { token },
    });
  } catch {
    showError(t('sms-code-invalid-or-expired'));
  } finally {
    isVerifying.value = false;
  }
}

async function onResend() {
  if (resendCooldown.value > 0 || isResending.value) return;
  if (!authStore.pendingSmsReset) {
    router.replace('/reset-password');
    return;
  }
  isResending.value = true;
  try {
    const { phoneNumberDir, phoneNumber } = authStore.pendingSmsReset;
    await authStore.requestSmsResetCode(phoneNumberDir, phoneNumber);
    showInfo(t('sms-code-resent'));
    secondsLeft.value = CODE_TTL_SECONDS;
    if (resendTimer) clearInterval(resendTimer);
    startResendCooldown();
  } catch {
    showError(t('sms-code-resend-failed'));
  } finally {
    isResending.value = false;
  }
}
</script>

<style scoped>
.card-body {
  background: var(--surface-secondary);
}
.sms-code-form {
  width: 460px;
  max-width: 100%;
}
.hint {
  font-size: var(--font-size-sm);
  margin-bottom: 1rem;
}
.code-inputs {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: center;
  margin: 1.25rem 0;
}
.code-digit {
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 24px;
  font-weight: var(--font-weight-medium);
  border: 1px solid var(--border-default);
  background: var(--surface-primary);
}
.code-digit:focus {
  outline: none;
  border-color: var(--text-secondary);
  box-shadow: 0 0 0 0.25rem rgba(100, 101, 104, 0.25);
}
.code-digit.is-invalid {
  border-color: var(--text-danger);
  background: var(--status-error-soft);
}
.countdown {
  font-size: var(--font-size-sm);
  margin-bottom: 1rem;
}
.actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}
.actions .btn {
  border-radius: var(--control-radius);
  padding: 4px 16px;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}
.actions .btn-outline-secondary {
  border: 1px solid grey;
  color: var(--text-primary);
}
.actions .btn-link {
  color: var(--text-secondary);
  font-weight: var(--font-weight-regular);
}
</style>
